import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileText, Upload, Image as ImageIcon } from "lucide-react";
import AppShell, { useAppUser } from "@/components/AppShell";
import { authApi, formatApiError } from "@/lib/api";
import { readFileAsDataUrl } from "@/lib/files";

export default function Pendencias() {
  const { user } = useAppUser() || {};
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await authApi().get("/boletins/me");
      setData(r.data);
    } catch {
      setData({ boletins: [], pendencia: false });
    }
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSaving(true);
    try {
      const dataUrl = await readFileAsDataUrl(file, 4);
      await authApi().post("/boletins", { file_name: file.name, file_type: file.type, data_base64: dataUrl });
      toast.success("Boletim enviado com sucesso.");
      await refresh();
    } catch (err) {
      toast.error(err.message || formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const pendente = !!data?.pendencia;

  return (
    <AppShell active="aluno">
      <section data-testid="pendencias-page">
        <h1 className="font-display text-2xl font-bold" data-testid="pendencias-title">Pendências</h1>
        <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2 mb-6">REGULARIZE SEU CADASTRO</p>

        {data === null ? (
          <p className="text-zinc-500 text-sm">Carregando...</p>
        ) : (
          <div className="space-y-6">
            <div
              className={`rounded-2xl border p-6 flex items-start gap-4 ${
                pendente ? "border-yellow-400/50 bg-yellow-400/10" : "border-green-500/40 bg-green-500/10"
              }`}
              data-testid="pendencias-status-card"
            >
              {pendente ? (
                <AlertTriangle size={22} className="text-yellow-400 shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 size={22} className="text-green-400 shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`font-display font-semibold ${pendente ? "text-yellow-300" : "text-green-300"}`} data-testid="pendencias-status-label">
                  {pendente ? "Você possui pendência de boletim escolar" : "Nenhuma pendência no momento"}
                </p>
                <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">
                  Alunos com até 16 anos devem enviar o boletim escolar a cada 6 meses. Após o envio, um instrutor da
                  sua unidade precisa validar o boletim — somente então a pendência é retirada. A pendência bloqueia
                  novas graduações e exibe um alerta na sua foto para a equipe.
                </p>
              </div>
            </div>

            <div className="bg-[#1C1C23] border border-white/10 rounded-2xl p-6" data-testid="pendencias-upload-card">
              <p className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">ENVIAR BOLETIM ESCOLAR</p>
              <p className="text-xs text-zinc-500 mb-4">Aceitos: PDF ou foto (JPG, PNG, WEBP) de até 4MB.</p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={saving}
                data-testid="pendencias-upload-button"
                className="inline-flex items-center gap-2 font-display font-semibold text-sm bg-gold text-obsidian px-6 py-3 rounded-full hover:bg-gold-amber disabled:opacity-60 transition-colors"
              >
                <Upload size={16} /> {saving ? "Enviando..." : "Selecionar Arquivo"}
              </button>
              <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={upload} data-testid="pendencias-file-input" />
            </div>

            <div data-testid="pendencias-history">
              <p className="font-mono text-xs tracking-[0.3em] text-gold mb-4">
                BOLETINS ENVIADOS {data.boletins ? `(${data.boletins.length})` : ""}
              </p>
              {(data.boletins || []).length === 0 ? (
                <div className="flex flex-col items-center gap-4 border border-dashed border-white/15 rounded-2xl py-14 text-zinc-500" data-testid="pendencias-empty">
                  <FileText size={30} />
                  <p className="text-sm">Nenhum boletim enviado ainda.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data.boletins.map((b, i) => (
                    <div key={b.id} className="bg-[#1C1C23] border border-white/10 rounded-xl px-5 py-4 flex items-center gap-4" data-testid={`pendencias-item-${i}`}>
                      {b.file_type === "application/pdf" ? (
                        <FileText size={22} className="text-crimson shrink-0" />
                      ) : (
                        <ImageIcon size={22} className="text-gold shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{b.file_name}</p>
                        <p className="font-mono text-[11px] text-zinc-500 mt-0.5">
                          Enviado em {new Date(b.uploaded_at).toLocaleDateString("pt-BR")}
                          {b.status === "validado" && b.validated_by ? ` · validado por ${b.validated_by}` : ""}
                        </p>
                      </div>
                      <span
                        className={`font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full border shrink-0 ${
                          b.status === "validado"
                            ? "border-green-500/50 text-green-400 bg-green-500/10"
                            : "border-yellow-400/50 text-yellow-300 bg-yellow-400/10"
                        }`}
                        data-testid={`pendencias-status-${i}`}
                      >
                        {b.status === "validado" ? "VALIDADO" : "AGUARDANDO VALIDAÇÃO"}
                      </span>
                      <a
                        href={b.data_base64}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`pendencias-open-${i}`}
                        className="font-mono text-[11px] tracking-widest border border-white/15 text-zinc-300 px-3 py-2 rounded-lg hover:border-gold/50 hover:text-gold transition-colors shrink-0"
                      >
                        ABRIR
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </AppShell>
  );
}
